import React, { useEffect, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { apiCall } from '../_shared';

const pctOf = (st) => (st?.windows_total ? Math.round(100 * (st.windows_done || 0) / st.windows_total) : null);

// Shown when the archive on this server is empty: start the Beds24 import right here and watch it fill up.
export const EmptyArchive = ({ onFilled }) => {
  const [st, setSt] = useState(null);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const running = st?.status === 'running';
  useEffect(() => {
    let timer = null;
    const poll = async () => {
      try {
        const r = await apiCall('/api/admin/beds24/import-bookings');
        setSt(r.state);
        if (r.state?.status !== 'running' && (r.summary?.total || 0) > 0) { onFilled?.(); return; }
      } catch (e) { setMsg(e.message); }
      timer = setTimeout(poll, running ? 4000 : 15000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [running, onFilled]);
  const start = async () => {
    setBusy(true); setMsg('');
    try {
      const from = `${new Date().getFullYear() - 1}-01-01`;
      const r = await apiCall(`/api/admin/beds24/import-bookings?from_date=${from}`, { method: 'POST' });
      setMsg(`Import started: ${r.from} → ${r.to} in ${r.windows} steps. This takes a few minutes; the page fills in automatically.`);
      setSt({ status: 'running', windows_total: r.windows, windows_done: 0 });
    } catch (e) { setMsg(e.message); } finally { setBusy(false); }
  };
  const p = pctOf(st);
  return (
    <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-5 text-sm text-amber-900" data-testid="stats-empty-archive">
      <p className="font-bold text-base">No bookings in this server's archive yet</p>
      <p className="mt-1 text-xs">The statistics are computed from a local copy of all Beds24 reservations. On this server that copy is still empty — import it once (from {new Date().getFullYear() - 1} to two years ahead); afterwards it refreshes itself every 10 minutes.</p>
      {running ? (
        <div className="mt-3" data-testid="stats-import-progress">
          <p className="text-xs font-semibold inline-flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Importing… {st.windows_done || 0}/{st.windows_total || '?'} steps{p != null ? ` · ${p}%` : ''}{st.upserted ? ` · ${st.upserted} bookings so far` : ''}</p>
          <div className="h-2 bg-white rounded-full mt-1 overflow-hidden"><div className="h-full bg-amber-500 transition-all" style={{ width: `${p ?? 5}%` }} /></div>
        </div>
      ) : (
        <button type="button" onClick={start} disabled={busy} className="mt-3 inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50" data-testid="stats-import-start"><Download className="w-4 h-4" /> Import bookings from Beds24 now</button>
      )}
      {st?.last_error && !running && <p className="text-xs text-red-700 mt-2" data-testid="stats-import-error">Last import: {st.last_error}</p>}
      {msg && <p className="text-xs mt-2" data-testid="stats-import-msg">{msg}</p>}
    </div>
  );
};
